import { useEffect, useState } from "react";
import { api, idr, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Activity, AlertTriangle, ShieldCheck, TrendingUp, Loader2, FileBarChart, MailSearch, BellRing, UserX } from "lucide-react";

const PERIODS = [["daily", "Harian"], ["weekly", "Mingguan"], ["monthly", "Bulanan"]];

function Stat({ icon: Icon, label, value, testid, tone = "" }) {
  return (
    <div className="border border-border bg-card rounded-sm p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground"><Icon className={`w-4 h-4 ${tone}`} /> {label}</div>
      <div className="font-heading font-black text-2xl mt-1">{value}</div>
    </div>
  );
}

export default function Manager() {
  const [ov, setOv] = useState(null);
  const [reports, setReports] = useState([]);
  const [busy, setBusy] = useState("");
  const [title, setTitle] = useState("Manager");
  useEffect(() => { api.get("/agents/team").then((r) => setTitle(r.data.agents.find((a) => a.key === "chandra")?.name || "Manager")).catch(() => {}); }, []);

  const load = () => {
    api.get("/manager/overview").then((r) => setOv(r.data)).catch(() => {});
    api.get("/manager/reports").then((r) => setReports(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const generate = async (period) => {
    setBusy(period);
    try { await api.post("/manager/report", { period }, { timeout: 180000 }); toast.success(`Laporan dibuat oleh ${title}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const runFollowup = async () => {
    setBusy("followup");
    try { const { data } = await api.post("/cases/followup/run"); toast.success(`${data.sent} pengingat terkirim · ${data.flagged} ditandai tanpa respon`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const pollEmail = async () => {
    setBusy("email");
    try { const { data } = await api.post("/cases/email/poll", {}, { timeout: 300000 }); toast.success(`${data.processed} email PO diproses`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div data-testid="manager-page">
      <PageHeader overline="Agent 7 · AI Operations Manager" title={title}>
        <Button variant="outline" className="rounded-sm" onClick={runFollowup} disabled={!!busy} data-testid="run-followup-btn">
          {busy === "followup" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BellRing className="w-4 h-4 mr-2" />} Jalankan Follow-up
        </Button>
        <Button variant="outline" className="rounded-sm" onClick={pollEmail} disabled={!!busy} data-testid="poll-email-btn">
          {busy === "email" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MailSearch className="w-4 h-4 mr-2" />} Cek Email PO
        </Button>
      </PageHeader>
      <div className="p-8 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <Stat icon={Activity} label="Total Open" value={ov?.total_open ?? "-"} testid="stat-open" />
          <Stat icon={AlertTriangle} label="Overdue SLA" value={ov?.total_overdue ?? "-"} testid="stat-overdue" tone="text-red-500" />
          <Stat icon={AlertTriangle} label="Eskalasi" value={ov?.escalated ?? "-"} testid="stat-escalated" tone="text-orange-500" />
          <Stat icon={ShieldCheck} label="Approval Menunggu" value={ov?.approvals_pending ?? "-"} testid="stat-approvals" tone="text-amber-600" />
          <Stat icon={UserX} label="Tanpa Respon" value={ov?.no_response ?? "-"} testid="stat-no-response" tone="text-red-500" />
          <Stat icon={TrendingUp} label={`Margin (${ov?.margin_percent ?? 0}%)`} value={ov ? idr(ov.margin_total) : "-"} testid="stat-margin" tone="text-accent" />
        </div>

        <div className="border border-border rounded-sm overflow-hidden" data-testid="agent-load-table">
          <table className="w-full text-sm">
            <thead className="bg-secondary"><tr className="text-left overline text-muted-foreground">
              <th className="px-4 py-3">Agent</th><th className="px-4 py-3">Peran</th><th className="px-4 py-3 text-right">Open</th><th className="px-4 py-3 text-right">Overdue</th>
            </tr></thead>
            <tbody className="divide-y divide-border">
              {(ov?.agents || []).map((a) => (
                <tr key={a.key} data-testid={`agent-load-${a.key}`}>
                  <td className="px-4 py-3 font-medium">{a.no}. {a.name}</td><td className="px-4 py-3 text-muted-foreground">{a.role}</td>
                  <td className="px-4 py-3 text-right font-mono">{a.open}</td>
                  <td className={`px-4 py-3 text-right font-mono ${a.overdue ? "text-red-600 font-bold" : ""}`}>{a.overdue}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {ov?.overdue_cases?.length > 0 && (
          <div className="border border-red-200 bg-red-50 rounded-sm p-4 text-sm" data-testid="overdue-list">
            <div className="font-bold text-red-700 mb-2">Kasus melewati SLA</div>
            {ov.overdue_cases.map((c) => <div key={c.id}>{c.case_number} · {c.customer} · {c.agent} · {c.priority}</div>)}
          </div>
        )}

        {ov?.no_response_cases?.length > 0 && (
          <div className="border border-orange-200 bg-orange-50 rounded-sm p-4 text-sm" data-testid="no-response-list">
            <div className="font-bold text-orange-700 mb-2">Customer belum merespon setelah follow-up</div>
            {ov.no_response_cases.map((c) => <div key={c.id}>{c.case_number} · {c.quote_number} · {c.customer} — {c.note}</div>)}
          </div>
        )}

        <div>
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <FileBarChart className="w-5 h-5 text-primary" /><h3 className="font-heading font-bold mr-2">Laporan untuk Direktur</h3>
            {PERIODS.map(([k, l]) => (
              <Button key={k} size="sm" variant="outline" className="rounded-sm" disabled={!!busy} onClick={() => generate(k)} data-testid={`report-${k}-btn`}>
                {busy === k && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} Buat Laporan {l}
              </Button>
            ))}
          </div>
          <div className="space-y-3">
            {reports.length === 0 && <div className="text-sm text-muted-foreground border border-dashed border-border rounded-sm p-6 text-center">Belum ada laporan.</div>}
            {reports.map((r) => (
              <details key={r.id} className="border border-border bg-card rounded-sm p-4" data-testid={`report-${r.id}`}>
                <summary className="cursor-pointer font-medium text-sm">Laporan {PERIODS.find((p) => p[0] === r.period)?.[1]} · {(r.created_at || "").slice(0, 16).replace("T", " ")}</summary>
                <div className="mt-3 text-sm whitespace-pre-wrap leading-relaxed">{r.content}</div>
              </details>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
